import { InfoRounded } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import LoadingButton from '@mui/lab/LoadingButton'
import { Autocomplete, Button, Divider, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../../../backend/ApiClient'
import FlowApiClient from '../../../../backend/sub_api_clients/FlowApiClient'
import { IFlow, INode, NodeType } from '../../../../definitions/Nodes'
import EventsEngine from '../../../../engines/EventsEngine'
import useCommand from '../../../hooks/useCommand'
import useInvalidate from '../../../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../../../hooks/useQuery'
import { snackbar } from '../../../services/SnackbarService'
import RenderTableSchema from '../../nodes/RenderTableSchema'

interface P {
    selectedNode: INode<NodeType>
    flow: IFlow
    setSelected: (node: INode) => void
    disable: boolean
    onClose: () => void
}

interface option {
    value: string
    label: string
}

const renderTooltip = (title: string, disable: boolean) => (
    <Tooltip arrow title={title} disableHoverListener={disable}>
        <InfoRounded sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
    </Tooltip>
)

function NodeSubFlowEditor({ selectedNode: node, flow, setSelected, disable, onClose }: P) {
    const myNode = node as INode<'exe_subFlow'>
    const [selectedField, setSelectedField] = useState<option>({ value: myNode.data.flowId, label: '' })
    const invalidateFlow = useInvalidate({ queryKey: buildQueryKey(`get-flow-${flow.uuid}`) })
    const invalidateSubFlow = useInvalidate({ queryKey: buildQueryKey('get-all-subflows') })

    const { status: statusAllSubFlows, response: allSubFlows } = useQuery({
        queryKey: buildQueryKey(`get-all-subflows`),
        queryFn: async () => await FlowApiClient.getSubFlows(),
        cache: true
    })

    const { status: statusSubFlowIO, response: subFlowIO } = useQuery({
        queryKey: buildQueryKey(`get-io-subflow-${myNode.uuid}`),
        queryFn: async () => await FlowApiClient.getSubFlowIO(selectedField.value, flow.uuid)
    })

    const { execute: handleNewSubFlow, status: statusNewSubFlow } = useCommand({
        commandFn: () => {
            const createdSubFlow = ApiClient.Flow.createSubFlow(flow.uuid, [], myNode.uuid)
            return createdSubFlow
        },
        onFinish: createdSubFlow => {
            if (createdSubFlow.status === 'success') {
                invalidateFlow()
                invalidateSubFlow()
                setSelectedField({ ...selectedField, value: createdSubFlow.response.uuid, label: createdSubFlow.response.name })
                EventsEngine.publish('add-flow-tab', { flowId: createdSubFlow.response.uuid, name: createdSubFlow.response.name })
                onClose()
            } else snackbar.show({ severity: 'error', text: createdSubFlow.errorText })
        }
    })

    useEffect(() => {
        const subFlowName = allSubFlows?.find(x => x?.uuid === selectedField?.value)?.name
        setSelectedField({ ...selectedField, label: subFlowName })
    }, [statusAllSubFlows === 'success'])

    const handleEditSubFlow = () => {
        EventsEngine.publish('add-flow-tab', { flowId: selectedField.value, name: selectedField.label })
        onClose()
    }

    const renderChoiceSubFlow = () => (
        <>
            <Stack flexDirection='row' gap={1}>
                {renderTooltip('', disable)}
                <Typography variant='subtitle2'>Search Subflow</Typography>
            </Stack>
            <Autocomplete
                disableClearable
                disabled={disable}
                size='small'
                options={allSubFlows.map(x => ({ value: x.uuid, label: x.name }))}
                value={selectedField}
                getOptionLabel={option => option.label || ''}
                onChange={(e, v: option) => {
                    e.preventDefault()
                    setSelectedField(v)
                    setSelected({ ...node, data: { ...node.data, flowId: v.value } })
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant='outlined'
                    />
                )}
            />
            <Stack marginTop={2} direction='row' spacing={1} sx={{ justifyContent: 'end', alignItems: 'center' }}>
                <LoadingButton
                    variant='outlined'
                    loading={statusNewSubFlow === 'loading'}
                    loadingPosition='start'
                    startIcon={<AddIcon />}
                    onClick={handleNewSubFlow}
                    disabled={disable}>
                    new subflow
                </LoadingButton>
                <Button
                    disabled={disable || selectedField.label === '' || selectedField.label === undefined}
                    variant='contained'
                    startIcon={<EditIcon />}
                    onClick={handleEditSubFlow}>
                    edit subflow
                </Button>
            </Stack>
        </>
    )

    const renderInputOutput = () => {
        const input = subFlowIO?.subFlow.nodes.find(n => n.type === 'subFlow_start')?.jsonSchema
        // @ts-ignore - exception for subflow, because JSON schema doesn't have value as field
        const output = subFlowIO?.subFlow.nodes.find(n => n.type === 'subFlow_end')?.jsonSchema?.value
        const loading = statusSubFlowIO !== 'success' && !input && !output
        return (
            <Stack marginTop={2} spacing={1}>
                <Typography variant='h6'>
                    {loading ? <Skeleton /> : 'Input'}
                </Typography>
                {loading ? <Skeleton height={100} /> : <RenderTableSchema data={input} />}
                <Typography variant='h6'>
                    {loading ? <Skeleton /> : 'Output'}
                </Typography>
                {loading ? <Skeleton height={100} /> : <RenderTableSchema data={output} />}
            </Stack>
        )
    }

    return (
        <Stack spacing={2}>
            {statusAllSubFlows === 'loading'
                ? (
                        <>
                            <Skeleton variant='rounded' width='100%' height='2.5em' sx={{ marginBottom: 2 }} />
                            <Stack direction='row' spacing={1} sx={{ justifyContent: 'end', alignItems: 'center' }}>
                                <Skeleton variant='rounded' width='10em' height='2em' />
                                <Skeleton variant='rounded' width='10em' height='2em' />
                            </Stack>
                        </>
                    )
                : renderChoiceSubFlow()}
            <Divider sx={{ marginTop: 2 }} />
            {renderInputOutput()}
        </Stack>
    )
}
export default NodeSubFlowEditor
